import type { PermissionAction, ScreenId } from "@agentcall/core/type/permission_type"
import { useAtomValue } from "jotai"
import { useEffect, useMemo, useState } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useSignOut } from "@/api/client/auth"
import { defaultMenuItems, type MenuItem, Sidebar } from "@/components/ui/sidebar/Sidebar"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { usePermission } from "@/features/auth/hooks/usePermission"
import {
  currentAdminAtom,
  isSessionValidAtom,
  sessionErrorAtom,
  sessionLoadingAtom,
  sessionUserAtom,
} from "@/store/session"

/**
 * メニューアイテムを権限に基づいてフィルタリング
 */
function filterMenuItemsByPermission(
  items: MenuItem[],
  can: (screenId: ScreenId, action: PermissionAction) => boolean
): MenuItem[] {
  const hasPermission = (item: MenuItem): boolean => {
    if (!item.screenId) return true
    const action = item.requiredAction || "read"
    return can(item.screenId, action)
  }

  return (
    items
      .filter(hasPermission)
      .map((item) => ({
        ...item,
        // 子要素もフィルタリング
        children: item.children ? item.children.filter(hasPermission) : undefined,
      }))
      // 子要素がすべてフィルタされた親は非表示
      .filter((item) => {
        if (item.children && item.children.length === 0) return false
        return true
      })
  )
}

export function MainLayout() {
  const navigate = useNavigate()
  const [activeId, setActiveId] = useState<string>("cases-list")
  const { can, canAccess, role: userRole, roleLabel } = usePermission()

  // 権限に基づいてメニューをフィルタリング
  const filteredMenuItems = useMemo(() => {
    return filterMenuItemsByPermission(defaultMenuItems, can)
  }, [can])

  const sessionUser = useAtomValue(sessionUserAtom)
  const isLoading = useAtomValue(sessionLoadingAtom)
  const error = useAtomValue(sessionErrorAtom)
  const isSessionValid = useAtomValue(isSessionValidAtom)

  // adminAPIから最新のユーザー情報を取得（jotai管理）
  const admin = useAtomValue(currentAdminAtom)

  const handleItemClick = (item: { id: string; label: string }) => {
    setActiveId(item.id)
  }

  // サイドバーに表示するユーザー情報（jotai管理のadminから取得）
  // adminが読み込まれるまではundefinedにして、sessionUserのnameを表示しない
  const displayUser = admin
    ? {
        name: admin.name || sessionUser?.email || "",
        role: roleLabel ?? "",
      }
    : undefined

  // プロフィール設定へのアクセス権限
  const canAccessProfile = canAccess("user_profile")

  // ログアウト処理
  const { mutateAsync: signOut } = useSignOut()
  const handleLogout = async () => {
    await signOut()
  }

  useEffect(() => {
    if (error) {
      toast.error(error.message || "セッション情報の取得に失敗しました。", {
        id: "session-error",
      })
    }
  }, [error])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 items-center justify-center min-h-screen">
        <Spinner className="text-slate-600 size-6 transition-opacity" />
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!isSessionValid || !sessionUser) {
    toast.error("ユーザー情報の取得に失敗しました。ログインしてください。", {
      id: "session-error",
    })
    navigate("/signin")
    return null
  }

  return (
    <div>
      <Sidebar
        items={filteredMenuItems}
        activeId={activeId}
        onItemClick={handleItemClick}
        user={displayUser}
        devRole={userRole}
        canAccessProfile={canAccessProfile}
        onLogout={handleLogout}
        className="h-full fixed top-0 left-0 z-10"
      />

      {/* Main content area */}
      <main className="pl-[235px]">
        <div className="bg-[#fafafb] min-h-screen min-w-[1200px] px-[30px] pt-[30px] pb-[60px]">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
